import { AdyenCheckout, Card } from "@adyen/adyen-web";
import { postMessageBetweenFrames } from "../../../utils/post-message-between-frames";
import { GraphQL, InternalTransactionsApiClient } from "../../internal-api";
import { CardProvider } from "../../services";
import {
  AdyenCardComponentFieldType,
  AdyenComponentDefaults,
} from "../adyen-config";
import { AdyenSessionResponse, InternalAdyenSessionsApiClient } from "../api";
import {
  PaymentSessionConfig,
  TokenizationSessionConfig,
  TransactionSessionData,
  CheckoutConfig,
  CardConfig,
  CardDetails,
  Contact,
  ErrorWithState,
} from "../index";

export class AdyenCardProvider implements CardProvider {
  private _paymentsClient?: InternalTransactionsApiClient;
  private _adyenClient?: InternalAdyenSessionsApiClient;
  private cardBrandRepresentationForAdyenCode: Record<string, string>;
  private adyenClientKey: string;
  private adyenEnvironment: string;
  private component: any;
  private sessionId?: string;
  private sessionResultPromise: Promise<any>;
  private resolveSessionResult!: (value: any) => void;
  private sessionResult?: {
    customerCreditCardId: string | null;
    refusal: string | null;
  };
  private brand?: string;
  private endDigits?: string;
  private encryptedExpiryDate?: string;
  private encryptedSecurityCode?: string;

  constructor(options: Options) {
    this.sessionResultPromise = new Promise((resolve) => {
      this.resolveSessionResult = resolve;
    });
    this.cardBrandRepresentationForAdyenCode =
      options.cardBrandRepresentationForAdyenCode;
    this.adyenClientKey = options.adyenClientKey;
    this.adyenEnvironment = options.adyenEnvironment;
  }

  private get paymentsClient(): InternalTransactionsApiClient {
    if (!this._paymentsClient) {
      throw new Error("paymentsClient accessed before being initialized");
    }
    return this._paymentsClient;
  }

  private get adyenClient(): InternalAdyenSessionsApiClient {
    if (!this._adyenClient) {
      throw new Error("adyenClient accessed before being initialized");
    }
    return this._adyenClient;
  }

  initializeApiClients(apiOrigin: string, token: string): void {
    this._paymentsClient = new InternalTransactionsApiClient(
      GraphQL.createClient(apiOrigin, token),
    );
    this._adyenClient = new InternalAdyenSessionsApiClient(apiOrigin, token);
  }

  private setComponent(component: any): void {
    this.component = component;
  }

  private async getAdyenSession(
    encodedSessionData: string,
  ): Promise<AdyenSessionResponse> {
    return this.adyenClient.createAdyenSession(encodedSessionData);
  }

  private async getAdyenTokenizationSession(
    encodedSessionData: string,
  ): Promise<AdyenSessionResponse> {
    return this.adyenClient.createAdyenTokenizationSession(encodedSessionData);
  }

  private async createPaymentSessionConfigurations({
    transactionSessionData,
    encodedSessionData,
    cardConfigurationData,
  }: PaymentSessionConfig) {
    const SHOW_PAY_BUTTON_BOOLEAN = false;
    const { id: sessionId, session_data: sessionData } =
      await this.getAdyenSession(encodedSessionData);
    this.sessionId = sessionId;

    const checkoutConfiguration = {
      clientKey: this.adyenClientKey,
      environment: this.adyenEnvironment,
      locale: AdyenComponentDefaults.locale,
      session: { id: sessionId, sessionData },
      onPaymentCompleted: this.getOnPaymentSuccessCallback(
        transactionSessionData.transactionId,
        sessionId,
      ),
      onPaymentFailed: this.getOnPaymentFailedCallback(),
      onError: this.getOnErrorCallback(),
      showPayButton: SHOW_PAY_BUTTON_BOOLEAN,
    };

    const cardConfiguration = {
      ...cardConfigurationData,
      ...(process.env.WEB_DROP_IN_SHOW_NAME
        ? {
            hasHolderName: true,
            holderNameRequired: true,
            autoFocus: true,
            positionHolderNameOnTop: true,
          }
        : {}),
      onFieldValid: this.getOnFieldValidCallback(),
      onBrand: this.getOnBrandCallback(),
    };

    return { checkoutConfiguration, cardConfiguration };
  }

  private async createTokenizationSessionConfigurations({
    encodedSessionData,
    cardConfigurationData,
  }: TokenizationSessionConfig) {
    const SHOW_PAY_BUTTON_BOOLEAN = false;
    const { id: sessionId, session_data: sessionData } =
      await this.getAdyenTokenizationSession(encodedSessionData);
    this.sessionId = sessionId;

    const checkoutConfiguration = {
      clientKey: this.adyenClientKey,
      environment: this.adyenEnvironment,
      locale: AdyenComponentDefaults.locale,
      session: { id: sessionId, sessionData },
      onPaymentCompleted: this.getOnTokenizationSuccessCallback(sessionId),
      onPaymentFailed: this.getOnPaymentFailedCallback(),
      onError: this.getOnErrorCallback(),
      showPayButton: SHOW_PAY_BUTTON_BOOLEAN,
    };

    const cardConfiguration = {
      ...cardConfigurationData,
      ...(process.env.WEB_DROP_IN_SHOW_NAME
        ? {
            hasHolderName: true,
            holderNameRequired: true,
            autoFocus: true,
            positionHolderNameOnTop: true,
          }
        : {}),
      onFieldValid: this.getOnFieldValidCallback(),
      onBrand: this.getOnBrandCallback(),
    };

    return { checkoutConfiguration, cardConfiguration };
  }

  private async mountAdyenComponent(
    checkoutConfiguration: any,
    cardConfiguration: any,
    componentRef: ComponentRef,
  ): Promise<void> {
    if (!componentRef.current) {
      throw new Error(
        "Adyen component is not able to mount when container reference is missing",
      );
    }

    const checkout = await AdyenCheckout(checkoutConfiguration);
    const card = new Card(checkout, cardConfiguration);
    card.mount(componentRef.current);
    this.setComponent(card);
  }

  private getOnPaymentSuccessCallback(
    transactionId: string,
    sessionId: string,
  ) {
    return async (data: any, component: any) => {
      if (!this.sessionResult) {
        const contact: Contact =
          await this.paymentsClient.getContact(transactionId);
        let session_result_response =
          await this.adyenClient.sendAdyenSessionResult(
            this.brand!,
            this.endDigits!,
            contact.identifier,
            transactionId,
            data.resultCode,
            sessionId,
          );
        this.sessionResult = {
          customerCreditCardId: session_result_response.customer_credit_card_id,
          refusal: null,
        };
        await this.paymentsClient.payTransactionViaCreditCard(
          transactionId,
          session_result_response.customer_credit_card_id,
        );
        this.resolveSessionResult(this.sessionResult);
        component.unmount();
      }
    };
  }

  private getOnTokenizationSuccessCallback(sessionId: string) {
    return async (data: any, component: any) => {
      if (!this.sessionResult) {
        this.sessionResult = {
          customerCreditCardId: null,
          refusal: null,
        };
        this.resolveSessionResult(this.sessionResult);
        component.unmount();
        postMessageBetweenFrames({ type: "card-tokenization-completed" });
      }
    };
  }

  private getOnPaymentFailedCallback() {
    return async (data: any, component: any) => {
      if (!this.sessionResult) {
        this.sessionResult = {
          customerCreditCardId: null,
          refusal: data.resultCode,
        };
        this.resolveSessionResult(this.sessionResult);
        component.unmount();
        postMessageBetweenFrames({ type: "card-payment-failed" });
      }
    };
  }

  private getOnErrorCallback() {
    return async (error: Error, component: any) => {
      console.error(error.name, error.message, error.stack, component);
      component.unmount();
      postMessageBetweenFrames({ type: "card-payment-failed" });
    };
  }

  private getOnFieldValidCallback() {
    const getAttributeDefinitionIfValid = (
      data: any,
      attribute: string,
    ): any => (data.valid ? data[attribute] : undefined);

    return async (data: any) => {
      switch (data.fieldType) {
        case AdyenCardComponentFieldType.ENCRYPTED_CARD_NUMBER:
          this.endDigits = getAttributeDefinitionIfValid(data, "endDigits");
          break;
        case AdyenCardComponentFieldType.ENCRYPTED_EXPIRY_DATE:
          this.encryptedExpiryDate = getAttributeDefinitionIfValid(
            data,
            "blob",
          );
          break;
        case AdyenCardComponentFieldType.ENCRYPTED_SECURITY_CODE:
          this.encryptedSecurityCode = getAttributeDefinitionIfValid(
            data,
            "blob",
          );
          break;
      }
    };
  }

  private getOnBrandCallback() {
    [];
    return async (data: any) => {
      this.brand = data.brand;
    };
  }

  private async getSessionResult(): Promise<{
    customerCreditCardId: string | null;
    refusal: string | null;
  }> {
    console.log("Waiting for session result to be set...");
    if (!this.sessionResult) {
      await this.sessionResultPromise;
    }
    console.log("Waiting for session result to be set...");
    return this.sessionResult!;
  }

  private getRawCardDetails() {
    return {
      brand: this.brand,
      endDigits: this.endDigits,
      expiryDate: this.encryptedExpiryDate,
      securityCode: this.encryptedSecurityCode,
    };
  }

  private async createPaymentConfigurationData(
    transactionId: string,
    encodedSessionData: string,
  ): Promise<PaymentSessionConfig> {
    const transactionSessionData: TransactionSessionData = {
      transactionId: transactionId,
      redirectUrl: AdyenComponentDefaults.redirectUrl,
      tokenizePaymentMethod: false,
    };

    const checkoutConfigurationData: CheckoutConfig = {
      clientKey: this.adyenClientKey,
      environment: this.adyenEnvironment,
      locale: AdyenComponentDefaults.locale,
    };

    const cardConfigurationData: CardConfig = {
      enableStoreDetails: AdyenComponentDefaults.enableStoreDetails,
    };

    return {
      transactionSessionData,
      encodedSessionData: encodedSessionData,
      checkoutConfigurationData,
      cardConfigurationData,
    };
  }

  private async createTokenizationConfigurationData(
    encodedSessionData: string,
  ): Promise<TokenizationSessionConfig> {
    const checkoutConfigurationData: CheckoutConfig = {
      clientKey: this.adyenClientKey,
      environment: this.adyenEnvironment,
      locale: AdyenComponentDefaults.locale,
    };

    const cardConfigurationData: CardConfig = {
      enableStoreDetails: AdyenComponentDefaults.enableStoreDetails,
    };

    return {
      encodedSessionData: encodedSessionData,
      checkoutConfigurationData,
      cardConfigurationData,
    };
  }

  public async paymentSetup(
    transactionId: string,
    encodedSessionData: string,
    componentRef: ComponentRef,
  ): Promise<void> {
    const sessionConfigurationData = await this.createPaymentConfigurationData(
      transactionId,
      encodedSessionData,
    );
    const { checkoutConfiguration, cardConfiguration } =
      await this.createPaymentSessionConfigurations(sessionConfigurationData);

    if (!componentRef.current) {
      console.warn(
        "Adyen component is not able to mount when container reference is missing",
      );
      return;
    }

    await this.mountAdyenComponent(
      checkoutConfiguration,
      cardConfiguration,
      componentRef,
    );
  }

  public async tokenizationSetup(
    encodedSessionData: string,
    componentRef: ComponentRef,
  ): Promise<void> {
    const sessionConfigurationData =
      await this.createTokenizationConfigurationData(encodedSessionData);
    const { checkoutConfiguration, cardConfiguration } =
      await this.createTokenizationSessionConfigurations(
        sessionConfigurationData,
      );

    if (!componentRef.current) {
      console.warn(
        "Adyen component is not able to mount when container reference is missing",
      );
      return;
    }

    await this.mountAdyenComponent(
      checkoutConfiguration,
      cardConfiguration,
      componentRef,
    );
  }

  public getBrand(): string | undefined {
    return this.cardBrandRepresentationForAdyenCode[this.brand!];
  }

  public getEndDigits(): string | undefined {
    return this.endDigits;
  }

  public getCardDetails(): CardDetails {
    return {
      brand: this.getBrand()!,
      last4: this.getEndDigits()!,
    };
  }

  public allCardDetailsAreDefined(): boolean {
    return Object.values(this.getRawCardDetails()).every(
      (v) => v !== undefined,
    );
  }

  public async submitSession(): Promise<{
    customerCreditCardId: string | null;
    refusal: string | null;
  }> {
    await this.adyenClient.claimSession(this.sessionId);

    if (!this.component?.state?.isValid) {
      const error:ErrorWithState = new Error("Cannot submit session: component state is not valid");
      error.state = {
        isValid: this.component?.state?.isValid,
        valid: this.component?.state?.valid,
      };

      throw error;
    }

    // submits the payment
    this.component.submit();

    console.log("Waiting for session result...");
    const sessionResult = await this.getSessionResult();
    console.log("Session result obtained.", { sessionResult });
    return sessionResult
  }
}

export namespace AdyenCardProvider {
  export const providerName = "adyen";

  export function create(options: Options): AdyenCardProvider {
    return new AdyenCardProvider(options);
  }

  export function register(): void {
    CardProvider.providers[providerName] = create;
  }

  export interface Options {
    paymentsClient: InternalTransactionsApiClient;
    adyenClient: InternalAdyenSessionsApiClient;
    cardBrandRepresentationForAdyenCode: Record<string, string>;
    readonly adyenClientKey: string;
    readonly adyenEnvironment: string;
  }
}

export const register = AdyenCardProvider.register;

type Options = AdyenCardProvider.Options;
